import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { disabledAdmin, enabledAdmin } from 'redux/actions/user';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BreakGlassDialog from 'components/BreakGlass/BreakGlassDialog';
import { getLoggedInUserDetails, sendBreakGlassLog } from 'utils/breakGlass';

const BREAK_GLASS_PAGE = 'settings-admin';

const ChangeAdminModal = ({ loading }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { currentBusiness } = useSelector(({ business }) => business);
  const loggedInUser = useSelector(({ user }) => user.data);

  const [changeAdmin, setChangeAdmin] = useState(false);
  const [breakGlassOpen, setBreakGlassOpen] = useState(false);

  const toggleChangeAdmin = () => setChangeAdmin(!changeAdmin);

  const willDisable = !currentBusiness.disabled;
  const actionSummary = `${willDisable ? 'Disable' : 'Enable'} account for "${
    currentBusiness?.companyName || ''
  }"`;

  const handleOpenBreakGlass = () => {
    setChangeAdmin(false);
    setBreakGlassOpen(true);
  };

  const handleAuthorized = (employeeName) => {
    const loggedInUserDetails = getLoggedInUserDetails(loggedInUser);

    Promise.resolve(
      dispatch(
        willDisable
          ? disabledAdmin(currentBusiness._id)
          : enabledAdmin(currentBusiness._id)
      )
    )
      .then(() => {
        sendBreakGlassLog(
          {
            eventType: 'admin-account-action',
            employeeName,
            actionSummary,
            result: 'success',
            loggedInUser: loggedInUserDetails,
          },
          BREAK_GLASS_PAGE
        );
        toast.success(
          `Account ${willDisable ? 'disabled' : 'enabled'} successfully.`
        );
      })
      .catch(() => {
        sendBreakGlassLog(
          {
            eventType: 'admin-account-action',
            employeeName,
            actionSummary,
            result: 'failed',
            loggedInUser: loggedInUserDetails,
          },
          BREAK_GLASS_PAGE
        );
        toast.error(`Failed to ${willDisable ? 'disable' : 'enable'} account.`);
      });
  };

  return (
    <>
      <LoadingButton
        variant="outlined"
        color={!currentBusiness.disabled ? 'error' : 'primary'}
        onClick={toggleChangeAdmin}
        loading={loading}
      >
        {!currentBusiness.disabled
          ? t('settings.disable_account')
          : t('settings.enable_account')}
      </LoadingButton>
      <Modal
        isOpen={changeAdmin}
        toggle={toggleChangeAdmin}
        className="changeAdmin-modal"
      >
        <ModalHeader
          toggle={toggleChangeAdmin}
          className="border-0"
        ></ModalHeader>
        <ModalBody className="border-0">
          <div className="d-flex flex-column text-center">
            <div>
              {t('settings.are_you_sure')}
              {!currentBusiness.disabled ? (
                <span className="ml-1">{t('settings.disable')}</span>
              ) : (
                <span className="ml-1">{t('settings.enable')}</span>
              )}
            </div>
            <div>
              <span className="font-weight-bold mr-1">
                {currentBusiness.companyName}
              </span>
              {t('settings.account_')}{' '}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="border-0">
          <div className=" d-flex ">
            <Button
              id="adminModalConect"
              outline
              color="primary"
              onClick={toggleChangeAdmin}
              className="mr-1"
            >
              {t('orders.cancel')}
            </Button>
            <Button
              id="adminModalDisable"
              type="button"
              color="primary"
              className={
                !currentBusiness.disabled ? 'disable-admin' : 'enable-admin'
              }
              onClick={handleOpenBreakGlass}
            >
              {!currentBusiness.disabled
                ? t('settings.disable')
                : t('settings.enable')}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
      <BreakGlassDialog
        open={breakGlassOpen}
        onClose={() => setBreakGlassOpen(false)}
        onAuthorized={handleAuthorized}
        page={BREAK_GLASS_PAGE}
        actionSummary={actionSummary}
        cancelLabel={t('orders.cancel')}
      />
    </>
  );
};

export default ChangeAdminModal;
